import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { CurrencyProvider } from '@/contexts/CurrencyContext'
import { HighContrastSuggestionBanner } from '@/components/HighContrastSuggestionBanner'
import { PwaBranding } from '@/components/PwaBranding'
import { startWebUpdates } from '@/lib/webUpdate'
import './index.css'

// Register the PWA service worker in REAL BROWSERS only — never in the Electron
// desktop shell, which has its own auto-update flow (a SW could serve stale
// assets there). This is what makes the POS installable on a tablet. It also
// watches for new builds so an always-open tab doesn't run last week's bundle
// (F-FE-N191, lib/webUpdate.ts; applied by WebUpdateBanner).
if (typeof navigator !== 'undefined' && !/electron/i.test(navigator.userAgent)) {
  startWebUpdates()
}

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Create a new router instance
const router = createRouter({ routeTree })

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <CurrencyProvider>
            <TooltipProvider delayDuration={200} skipDelayDuration={0}>
              <RouterProvider router={router} />
            </TooltipProvider>
          </CurrencyProvider>
          <PwaBranding />
          <HighContrastSuggestionBanner />
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

